/**
 * A date range a report is read over, and the range it is compared against.
 *
 * PURE, and separate from `period.ts` ON PURPOSE. An accounting PERIOD is the calendar month an
 * agency signs off and locks — a regulated unit with a status, invariant 6, and consequences for
 * anything posted into it. A RANGE is a question somebody asked on a screen: "how did we do over
 * these dates". They must not share a type, because a range is arbitrary, unaudited and may cross
 * a lock boundary without meaning anything by it.
 *
 * WHAT A RANGE MAY AND MAY NOT SCOPE is the whole reason this file has a doc comment. A count of
 * things that HAPPENED — receipts posted, bank lines ingested, rent charged, fees earned — is a
 * fact about a span of days, and narrowing it to one is honest. A BALANCE is not: the trust
 * position, the arrears register, the unmatched queue and the three-way reconciliation are all
 * "as at now", and re-stating them "as at 31 July" would need a balance-as-at-date read of the
 * whole ledger. A dashboard that silently applied a date filter to some cards and not others
 * would be the worst version of this feature, so the caller must label which is which.
 */

import { addDays, daysBetween, isCalendarDate } from './dates.js';
import { periodEndsOn, periodStartFor, previousPeriodStart } from './period.js';

/** Both ends INCLUSIVE. A range of one day has `from === to` and covers that day. */
export interface DateRange {
  from: string;
  to: string;
}

/**
 * The ranges offered as one click. Everything else is two dates typed by hand.
 *
 * `this_month` is the default because it is what an agency's month-end rhythm runs on, and it is
 * what the bar showed before it did anything.
 */
export const DASHBOARD_RANGE_KEYS = [
  'this_month',
  'last_month',
  'last_30_days',
  'last_90_days',
  'quarter_to_date',
  'year_to_date',
] as const;

export type DashboardRangeKey = (typeof DASHBOARD_RANGE_KEYS)[number];

export const DASHBOARD_RANGE_LABELS: Readonly<Record<DashboardRangeKey, string>> = {
  this_month: 'This month',
  last_month: 'Last month',
  last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days',
  quarter_to_date: 'Quarter to date',
  year_to_date: 'Year to date',
};

/** The range this product opens on when nobody has chosen one. */
export const DEFAULT_DASHBOARD_RANGE: DashboardRangeKey = 'this_month';

/**
 * A preset, resolved against the day it is being asked on.
 *
 * `today` IS PASSED IN, never read from a clock, for the reason every date in this package is:
 * the server runs in UTC and an Auckland evening is already tomorrow there for half the year. The
 * caller supplies the NZ day.
 *
 * THE "TO DATE" PRESETS END TODAY rather than at the end of the month, quarter or year. A range
 * running to 31 December would count eleven months of nothing as part of the window and make
 * every comparison meaningless from January onwards.
 */
export function dashboardRange(key: DashboardRangeKey, today: string): DateRange {
  switch (key) {
    case 'this_month':
      return { from: periodStartFor(today), to: today };

    case 'last_month': {
      const start = previousPeriodStart(periodStartFor(today));
      return { from: start, to: periodEndsOn(start) };
    }

    // Inclusive of today, so "last 30 days" is thirty days of which today is the last — not
    // thirty days ending yesterday plus today making thirty-one.
    case 'last_30_days':
      return { from: addDays(today, -29), to: today };

    case 'last_90_days':
      return { from: addDays(today, -89), to: today };

    case 'quarter_to_date':
      return { from: quarterStart(today), to: today };

    case 'year_to_date':
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
  }
}

/** The first day of the calendar quarter a date falls in. Jan, Apr, Jul, Oct. */
function quarterStart(date: string): string {
  const month = Number(date.slice(5, 7));
  const first = month - ((month - 1) % 3);
  return `${date.slice(0, 4)}-${String(first).padStart(2, '0')}-01`;
}

/** Inclusive length in days. `daysBetween` already counts both ends. */
export function rangeDays(range: DateRange): number {
  return daysBetween(range.from, range.to);
}

/**
 * What a range is compared against: the SAME NUMBER OF DAYS, ending the day before it starts.
 *
 * ONE RULE, DELIBERATELY, and it is worth saying why the obvious alternative was not taken. The
 * intuitive comparison for "this month" is "last month" — but on the 19th that compares nineteen
 * days against thirty-one, and every agency would appear to have collapsed. Equal-length windows
 * are the only comparison a COUNT can be read against, and February against January would mislead
 * the same way for the same reason.
 *
 * The cost is that the comparison window is not month-aligned mid-month, which is why every
 * caller must SHOW ITS DATES rather than calling it "last month". A comparison whose span the
 * reader cannot see is a number they cannot check — principle 4.
 */
export function comparisonRange(range: DateRange): DateRange {
  const to = addDays(range.from, -1);
  return { from: addDays(to, -(rangeDays(range) - 1)), to };
}

/** True when both ends are real dates and the range does not run backwards. */
export function isDateRange(value: { from: string; to: string }): boolean {
  return isCalendarDate(value.from) && isCalendarDate(value.to) && value.from <= value.to;
}

/**
 * Which preset a range IS, if any — so a menu can tick the one in force.
 *
 * Compared by the dates rather than remembered as a key, because the URL carries dates and a
 * remembered key would go stale the moment the day rolled over on a tab left open overnight.
 * Returns null for a range somebody typed, which is a real state and reads as "Custom".
 */
export function matchingRangeKey(range: DateRange, today: string): DashboardRangeKey | null {
  return (
    DASHBOARD_RANGE_KEYS.find((key) => {
      const preset = dashboardRange(key, today);
      return preset.from === range.from && preset.to === range.to;
    }) ?? null
  );
}

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** `2026-09-19` → `19 Sep 2026`. Hardcoded month names for the reason `formatPeriod` has them. */
function longDay(date: string): string {
  const month = SHORT_MONTHS[Number(date.slice(5, 7)) - 1];
  return `${Number(date.slice(8, 10))} ${month ?? date.slice(5, 7)} ${date.slice(0, 4)}`;
}

/**
 * A range as an operator reads it, with everything shared between the ends said once.
 *
 *   1 – 19 Sep 2026          same month
 *   1 Aug – 19 Sep 2026      same year
 *   1 Dec 2025 – 19 Jan 2026 neither
 *
 * NOT `formatNzDate`, which is `dd/mm/yyyy` and is right for a table column where the reader is
 * scanning for one row. This is a heading somebody reads once, where `19/09/2026 – 30/09/2026`
 * takes a second look to see which end moved.
 */
export function formatDateRange(range: DateRange): string {
  const sameYear = range.from.slice(0, 4) === range.to.slice(0, 4);
  const sameMonth = sameYear && range.from.slice(5, 7) === range.to.slice(5, 7);

  if (sameMonth) return `${Number(range.from.slice(8, 10))} – ${longDay(range.to)}`;
  if (sameYear) {
    const from = longDay(range.from);
    // Drop the trailing year from the first end only — the one on the second covers both.
    return `${from.slice(0, from.lastIndexOf(' '))} – ${longDay(range.to)}`;
  }
  return `${longDay(range.from)} – ${longDay(range.to)}`;
}

// ── Which overview ───────────────────────────────────────────────────────────

/**
 * The two dashboards — the money, and the houses — and which one a person's Dashboard opens on.
 *
 * Stored per USER (`users.dashboard_layout`, 0122), so the choice follows somebody from the office
 * machine to their laptop. `users.drift.test.ts` pins the column's enum to this list.
 */
export const DASHBOARD_LAYOUTS = ['finance', 'management', 'custom'] as const;
export type DashboardLayout = (typeof DASHBOARD_LAYOUTS)[number];

export const DASHBOARD_LAYOUT_LABELS: Readonly<Record<DashboardLayout, string>> = {
  finance: 'Finance',
  management: 'Property management',
  // The person's own arrangement of widgets from both — see `dashboardWidgets.ts`.
  custom: 'Custom',
};
